import type {
  ClienteRelatorioClientes,
  RelatorioClientes,
} from '@/features/relatorios/api/relatoriosApi';
import type { Tabela } from '@/shared/format/exportar';
import { formatarTelefone } from '@/shared/format/mascaras';

// =====================================================================
// Lógica pura do relatório de clientes: ordenação, formatação e montagem
// das tabelas de exportação (CSV / impressão A4). Sem React, sem API —
// é o que os testes cobrem.
// =====================================================================

export type CampoOrdenacaoClientes =
  | 'total'
  | 'servicos'
  | 'pecas'
  | 'ordens'
  | 'ultimaVisita'
  | 'nome';

export type DirecaoOrdenacao = 'asc' | 'desc';

export type OrdenacaoClientes = { campo: CampoOrdenacaoClientes; direcao: DirecaoOrdenacao };

export const ORDENACAO_PADRAO: OrdenacaoClientes = { campo: 'total', direcao: 'desc' };

export const ROTULOS_ORDENACAO: Record<CampoOrdenacaoClientes, string> = {
  total: 'Total gasto',
  servicos: 'Serviços',
  pecas: 'Peças',
  ordens: 'Nº de O.S.',
  ultimaVisita: 'Última visita',
  nome: 'Nome',
};

/** Nome sobe em A→Z ao clicar; valores e datas começam do maior. */
export function direcaoInicial(campo: CampoOrdenacaoClientes): DirecaoOrdenacao {
  return campo === 'nome' ? 'asc' : 'desc';
}

/** Clique no cabeçalho: mesma coluna inverte; outra coluna começa na direção natural. */
export function alternarOrdenacao(
  atual: OrdenacaoClientes,
  campo: CampoOrdenacaoClientes,
): OrdenacaoClientes {
  if (atual.campo === campo) {
    return { campo, direcao: atual.direcao === 'asc' ? 'desc' : 'asc' };
  }
  return { campo, direcao: direcaoInicial(campo) };
}

function valorOrdenacao(c: ClienteRelatorioClientes, campo: CampoOrdenacaoClientes): number {
  switch (campo) {
    case 'total':
      return c.total;
    case 'servicos':
      return c.totalServicos;
    case 'pecas':
      return c.totalPecas;
    case 'ordens':
      return c.quantidadeOrdens;
    case 'ultimaVisita':
      return c.ultimaVisita ? new Date(c.ultimaVisita).getTime() : Number.NEGATIVE_INFINITY;
    default:
      return 0;
  }
}

const compararNome = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' });

/** Ordena sem mutar. Empate desempata pelo nome (A→Z), sempre. */
export function ordenarClientes(
  lista: readonly ClienteRelatorioClientes[],
  ordem: OrdenacaoClientes,
): ClienteRelatorioClientes[] {
  const sinal = ordem.direcao === 'asc' ? 1 : -1;
  return [...lista].sort((a, b) => {
    if (ordem.campo === 'nome') return sinal * compararNome(a.nome, b.nome);
    const va = valorOrdenacao(a, ordem.campo);
    const vb = valorOrdenacao(b, ordem.campo);
    if (va !== vb) {
      // Sem visita fica sempre no fim, qualquer que seja a direção.
      if (va === Number.NEGATIVE_INFINITY) return 1;
      if (vb === Number.NEGATIVE_INFINITY) return -1;
      return sinal * (va - vb);
    }
    return compararNome(a.nome, b.nome);
  });
}

const fmtData = new Intl.DateTimeFormat('pt-BR');

export function formatarData(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : fmtData.format(d);
}

export function formatarTelefoneCliente(tel: string | null | undefined): string {
  const t = (tel ?? '').trim();
  if (!t) return '';
  return formatarTelefone(t) || t;
}

/** CPF/CNPJ formatado quando tem 11/14 dígitos; senão devolve como veio. */
export function formatarDocumento(doc: string | null | undefined): string {
  const d = (doc ?? '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return (doc ?? '').trim();
}

export function formatarKm(km: number | null | undefined): string {
  return km == null ? '—' : `${Math.trunc(km).toLocaleString('pt-BR')} km`;
}

/** Fração (0..1) das peças no total — para a barra peças × serviços. */
export function fracaoPecas(c: { totalPecas: number; totalServicos: number }): number {
  const soma = c.totalPecas + c.totalServicos;
  if (soma <= 0) return 0;
  return Math.min(1, Math.max(0, c.totalPecas / soma));
}

const moeda = (v: number) => v.toFixed(2);

/**
 * Tabelas de exportação/impressão: resumo, uma linha por cliente e o
 * detalhe cliente × veículo. Respeita a ordenação que está na tela.
 */
export function montarTabelasClientes(
  dados: RelatorioClientes | null,
  opts: { rotuloPeriodo: string; ordenacao?: OrdenacaoClientes; filtros?: string[] },
): Tabela[] {
  const extras = opts.filtros && opts.filtros.length > 0 ? ` • ${opts.filtros.join(' • ')}` : '';
  if (!dados) {
    return [
      {
        titulo: `Clientes — ${opts.rotuloPeriodo}${extras}`,
        cabecalho: ['Aviso'],
        linhas: [['Relatório ainda não carregado.']],
      },
    ];
  }
  const r = dados.resumo;
  const clientes = ordenarClientes(dados.clientes, opts.ordenacao ?? ORDENACAO_PADRAO);

  const resumo: Tabela = {
    titulo: `Clientes — resumo — ${opts.rotuloPeriodo}${extras}`,
    cabecalho: ['Indicador', 'Valor'],
    linhas: [
      ['Clientes atendidos', r.clientesComCompra],
      ['Clientes listados', r.clientes],
      ['Veículos atendidos', r.veiculos],
      ['Ordens de serviço', r.ordens],
      ['Total em serviços (R$)', moeda(r.totalServicos)],
      ['Total em peças (R$)', moeda(r.totalPecas)],
      ['Descontos concedidos (R$)', moeda(r.descontos)],
      ['Total (R$)', moeda(r.total)],
      ['Ticket médio por O.S. (R$)', moeda(r.ticketMedio)],
    ],
  };

  const porCliente: Tabela = {
    titulo: 'Gasto por cliente',
    cabecalho: [
      'Cliente',
      'Telefone',
      'Documento',
      'Veículos',
      'O.S.',
      'Última visita',
      'Serviços',
      'Peças',
      'Total',
      'Ticket médio',
    ],
    colunasNumero: [3, 4],
    colunasMoeda: [6, 7, 8, 9],
    linhas: clientes.map((c) => [
      c.nome,
      formatarTelefoneCliente(c.telefone),
      formatarDocumento(c.documento),
      c.quantidadeVeiculos,
      c.quantidadeOrdens,
      formatarData(c.ultimaVisita),
      moeda(c.totalServicos),
      moeda(c.totalPecas),
      moeda(c.total),
      moeda(c.ticketMedio),
    ]),
    rodape: [
      `${clientes.length} cliente(s)`,
      '',
      '',
      r.veiculos,
      r.ordens,
      '',
      moeda(r.totalServicos),
      moeda(r.totalPecas),
      moeda(r.total),
      moeda(r.ticketMedio),
    ],
  };

  const detalhe: Tabela = {
    titulo: 'Gasto por cliente e veículo',
    cabecalho: ['Cliente', 'Veículo', 'Placa', 'KM atual', 'O.S.', 'Última visita', 'Serviços', 'Peças', 'Total'],
    colunasNumero: [4],
    colunasMoeda: [6, 7, 8],
    linhas: clientes.flatMap((c) =>
      c.veiculos.map((v) => [
        c.nome,
        v.descricao,
        v.placa ?? '—',
        formatarKm(v.kmAtual),
        v.quantidadeOrdens,
        formatarData(v.ultimaVisita),
        moeda(v.totalServicos),
        moeda(v.totalPecas),
        moeda(v.total),
      ]),
    ),
    rodape: ['Totais', '', '', '', r.ordens, '', moeda(r.totalServicos), moeda(r.totalPecas), moeda(r.total)],
  };

  return [resumo, porCliente, detalhe];
}
